import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthActions, useAuthToken } from '@convex-dev/auth/react';
import { useMutation } from 'convex/react';
import { api } from '../convex/_generated/api';
import { API } from '../lib/api';

const FIRST_TOUCH_REFERRAL_STORAGE_KEY = 'dockclaw_first_touch_referral';
const FIRST_TOUCH_REFERRAL_WINDOW_MS = 365 * 24 * 60 * 60 * 1000;
const USER_CACHE_KEY = 'dockclaw_user_cache';

const readCachedUser = () => {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Minimal validation — anything without an email/_id is stale garbage.
    if (!parsed || !parsed.email || !parsed._id) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeCachedUser = (user) => {
  try {
    if (user && user.email && user._id) {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_CACHE_KEY);
    }
  } catch {
    // Ignore quota errors.
  }
};

const clearCachedUser = () => {
  try {
    localStorage.removeItem(USER_CACHE_KEY);
  } catch {
    // Ignore.
  }
};

const AuthContext = createContext(null);

const normalizeReferralCode = (rawCode = '') => rawCode.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

const hydrateReferralRecord = (record) => {
  if (!record) {
    return null;
  }

  return {
    ...record,
    expiresAt: record.capturedAt + FIRST_TOUCH_REFERRAL_WINDOW_MS,
    isConsumed: Boolean(record.consumedAt),
  };
};

const readFirstTouchReferral = () => {
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_REFERRAL_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    const code = normalizeReferralCode(parsed?.code || '');
    const capturedAt = Number(parsed?.capturedAt);
    const consumedAt = parsed?.consumedAt ? Number(parsed.consumedAt) : null;

    if (!code || !Number.isFinite(capturedAt)) {
      localStorage.removeItem(FIRST_TOUCH_REFERRAL_STORAGE_KEY);
      return null;
    }

    if (Date.now() - capturedAt >= FIRST_TOUCH_REFERRAL_WINDOW_MS) {
      localStorage.removeItem(FIRST_TOUCH_REFERRAL_STORAGE_KEY);
      return null;
    }

    const record = {
      code,
      capturedAt,
      consumedAt: Number.isFinite(consumedAt) ? consumedAt : null,
    };

    return hydrateReferralRecord(record);
  } catch (error) {
    localStorage.removeItem(FIRST_TOUCH_REFERRAL_STORAGE_KEY);
    return null;
  }
};

const writeFirstTouchReferral = (record) => {
  localStorage.setItem(
    FIRST_TOUCH_REFERRAL_STORAGE_KEY,
    JSON.stringify({
      code: record.code,
      capturedAt: record.capturedAt,
      consumedAt: record.consumedAt ?? null,
    })
  );
};

const markFirstTouchReferralConsumed = () => {
  const existing = readFirstTouchReferral();
  if (!existing) {
    return null;
  }
  localStorage.removeItem(FIRST_TOUCH_REFERRAL_STORAGE_KEY);
  return null;
};

// Module-level axios interceptor: sends auth token via both standard and custom headers.
// The exe.dev HTTPS proxy strips the standard Authorization header, so we also
// send X-Auth-Token which nginx converts back to Authorization before proxying.
axios.interceptors.request.use((config) => {
  const t = localStorage.getItem('convex_token');
  if (t) {
    config.headers.Authorization = `Bearer ${t}`;
    config.headers['X-Auth-Token'] = `Bearer ${t}`;
  }
  return config;
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Hydrate user from localStorage so dashboard pages render immediately on
  // reload instead of blocking every navigation on an /auth/me round-trip.
  // We still revalidate in the background (see fetchUser below).
  const initialToken = localStorage.getItem('convex_token');
  const initialUser = initialToken ? readCachedUser() : null;
  const [user, setUser] = useState(initialUser);
  const [token, setToken] = useState(initialToken);
  // If we have both a token and a cached user, skip the loading spinner —
  // the page can render with stale data while /auth/me refreshes in the background.
  const [loading, setLoading] = useState(!(initialToken && initialUser));
  const [syncAttempted, setSyncAttempted] = useState(false);
  const [firstTouchReferral, setFirstTouchReferral] = useState(() => readFirstTouchReferral());

  // Convex Auth
  const { signIn: convexSignIn, signOut: convexSignOut } = useAuthActions();
  const convexToken = useAuthToken();

  // Convex sync mutation
  const syncAuthUserMutation = useMutation(api.auth.syncAuthUser);

  // Handle loading state — if no token at all, stop loading immediately
  // Google OAuth sync is handled by ConvexAuthSync -> AuthCallback in App.js
  useEffect(() => {
    if (!convexToken && !token) {
      setLoading(false);
    }
  }, [convexToken, token]);

  const refreshFirstTouchReferral = useCallback(() => {
    const record = readFirstTouchReferral();
    setFirstTouchReferral(record);
    return record;
  }, []);

  const captureReferralCode = useCallback((rawCode) => {
    const existingRecord = readFirstTouchReferral();
    if (existingRecord) {
      setFirstTouchReferral(existingRecord);
      return existingRecord;
    }

    const code = normalizeReferralCode(rawCode);
    if (!code) {
      return null;
    }

    const record = hydrateReferralRecord({
      code,
      capturedAt: Date.now(),
      consumedAt: null,
    });

    writeFirstTouchReferral(record);
    setFirstTouchReferral(record);
    return record;
  }, []);

  const consumeFirstTouchReferral = useCallback(() => {
    const updated = markFirstTouchReferralConsumed();
    setFirstTouchReferral(updated);
    return updated;
  }, []);

  const logout = useCallback(async () => {
    // Sign out from Convex Auth
    try {
      await convexSignOut();
    } catch (e) {
      console.error('Convex signout error:', e);
    }
    localStorage.removeItem('convex_token');
    clearCachedUser();
    setToken(null);
    setUser(null);
    setSyncAttempted(false);

    // PostHog: reset identity on logout
    if (window.posthog) {
      window.posthog.reset();
    }
  }, [convexSignOut]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      writeCachedUser(response.data);

      // PostHog: identify on session restore / Google OAuth
      if (window.posthog && response.data?.email) {
        window.posthog.identify(response.data.email, {
          email: response.data.email,
          name: response.data.name,
        });
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  // Fetch user when we have a token (revalidates cached user in background)
  useEffect(() => {
    if (token) {
      fetchUser();
    }
  }, [token, fetchUser]);

  const register = async (email, password, name, referralRecord = null) => {
    const activeReferral = referralRecord?.code ? referralRecord : refreshFirstTouchReferral();
    const payload = {
      email,
      password,
      name,
    };

    if (activeReferral?.code) {
      payload.referral_code = activeReferral.code;
    }

    const response = await axios.post(`${API}/auth/register`, payload);
    const { token: newToken } = response.data;
    localStorage.setItem('convex_token', newToken);
    setToken(newToken);

    // PostHog: identify new user
    if (window.posthog) {
      window.posthog.identify(email, { email, name });
      window.posthog.capture('user_registered', { signup_method: 'email' });
    }

    if (response.data?.referral_attachment?.attached) {
      consumeFirstTouchReferral();
    }

    return response.data;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, {
      email,
      password
    });
    const { token: newToken } = response.data;
    localStorage.setItem('convex_token', newToken);
    setToken(newToken);

    // PostHog: identify returning user
    if (window.posthog) {
      window.posthog.identify(email, { email });
      window.posthog.capture('user_logged_in', { login_method: 'email' });
    }

    return response.data;
  };

  // Google OAuth login using Convex Auth
  const initiateGoogleLogin = useCallback(async (rawReferralCode = null) => {
    setSyncAttempted(false); // Reset sync flag
    try {
      if (rawReferralCode) {
        captureReferralCode(rawReferralCode);
      } else {
        refreshFirstTouchReferral();
      }

      // Convex auth validates redirectTo against configured SITE_URL.
      // Use REACT_APP_SITE_URL env var to set the canonical origin per environment.
      // This avoids www/non-www mismatch in production and ensures dev/preview work correctly.
      const isLocalDev = ['localhost', '127.0.0.1'].includes(window.location.hostname);
      const redirectTo = isLocalDev
        ? window.location.origin
        : (process.env.REACT_APP_SITE_URL || 'https://dockclaw.com');
      await convexSignIn("google", { redirectTo });
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  }, [captureReferralCode, convexSignIn, refreshFirstTouchReferral]);

  const value = {
    user,
    token,
    loading,
    firstTouchReferral,
    isAuthenticated: !!token || !!convexToken,
    register,
    login,
    logout,
    initiateGoogleLogin,
    captureReferralCode,
    refreshFirstTouchReferral,
    consumeFirstTouchReferral,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
